// ════════════════════════════════════════════════════════════════════
// NoteTemplatePicker
// "+ Insert Template" button + dropdown grouped by category.
// Embeds in TreatmentRecord → Clinical Notes. Clicking a template
// returns its body via the onInsert callback — caller decides whether
// to append or replace the field.
// ════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  surface: "#ffffff", card: "#ffffff", border: "#e8e4df",
  borderGold: "rgba(180,140,60,0.3)", goldBg: "rgba(180,140,60,0.08)",
  gold: "#b48c3c", goldD: "#8a6f2e",
  text: "#1a1a2e", muted: "#6b7280", dim: "#9ca3af", light: "#f3f4f6",
};
const F = "'Montserrat',sans-serif";

export default function NoteTemplatePicker({ onInsert, mode = "append" }) {
  // mode: "append" (default) — adds to existing content with a blank line
  //       "replace" — replaces whatever is currently in the field
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      const { data } = await supabase.from("note_templates")
        .select("id,category,name,body,display_order")
        .eq("active", true)
        .order("category").order("display_order").order("name");
      setItems(data || []);
      setLoading(false);
    })();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = items.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.name || "").toLowerCase().includes(q) ||
           (t.category || "").toLowerCase().includes(q);
  });
  const grouped = {};
  for (const r of filtered) (grouped[r.category] ||= []).push(r);

  const pick = (t) => {
    onInsert?.(t.body || "", t);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={wrapRef} style={{position: "relative", display: "inline-block"}}>
      <button onClick={() => setOpen(o => !o)} type="button"
        style={{
          padding: "7px 14px",
          background: open ? C.gold : C.goldBg,
          color: open ? "#fff" : C.goldD,
          border: "1px solid " + (open ? C.gold : C.borderGold),
          borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer",
          fontFamily: F, display: "inline-flex", alignItems: "center", gap: 6,
        }}>
        <span>+ Insert Template</span>
        <span style={{fontSize: 10, opacity: 0.7}}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 5000,
          width: 360, maxHeight: 460, overflow: "hidden",
          background: C.surface, border: "1px solid " + C.borderGold, borderRadius: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: "column",
        }}>
          <div style={{padding: "10px 12px", borderBottom: "1px solid " + C.border, background: C.goldBg}}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates…"
              style={{width: "100%", padding: "7px 10px", background: "#fff", border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, fontFamily: F, boxSizing: "border-box"}}/>
          </div>
          <div style={{overflowY: "auto", flex: 1}}>
            {loading && <div style={{padding: 16, textAlign: "center", color: C.muted, fontSize: 12}}>Loading…</div>}
            {!loading && Object.keys(grouped).length === 0 && (
              <div style={{padding: 20, textAlign: "center", color: C.dim, fontSize: 12}}>No templates match</div>
            )}
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat}>
                <div style={{padding: "8px 12px 4px", fontSize: 10, fontWeight: 800, color: C.gold, textTransform: "uppercase", letterSpacing: "0.07em", background: C.light, borderTop: "1px solid " + C.border}}>
                  {cat}
                </div>
                {list.map(t => (
                  <button key={t.id} onClick={() => pick(t)} type="button"
                    style={{
                      width: "100%", textAlign: "left", padding: "9px 14px",
                      background: "transparent", border: "none", borderBottom: "1px solid " + C.light,
                      cursor: "pointer", fontFamily: F,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = C.goldBg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 2}}>{t.name}</div>
                    <div style={{fontSize: 10.5, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{(t.body || "").replace(/\s+/g, " ").slice(0, 80)}…</div>
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div style={{padding: "6px 12px", borderTop: "1px solid " + C.border, background: C.light, fontSize: 10, color: C.muted, textAlign: "center"}}>
            {mode === "replace" ? "Replaces current note" : "Appends to current note"} · Edit templates via Patient Paperwork
          </div>
        </div>
      )}
    </div>
  );
}
